// hooks/useCalendar.ts
import { useEffect, useRef, useTransition } from 'react'
import dayjs, { DayjsInput } from 'utils/dayjs'
import { UseCalendarOptions } from '../types'
import { DATE_FORMAT } from '~constants'
import { useSearchParams } from 'next/navigation'
import { generateCalendarStartAndEndDate } from '../utils'
import { useEventsStore } from 'store/useEventsStore'

export const useCalendar = (options: UseCalendarOptions) => {
  const { initialDate = new Date(), artists = [], endDate } = options
  const searchParams = useSearchParams()
  const [_, startTransition] = useTransition()

  const initializedFromURL = useRef(false)

  const {
    selectedDate,
    selectedMonth,
    loading,
    errors,
    formattedEvents: filteredItems,
    activeMonthList,
    setSelectedDate,
    fetchEvents,
    setStartDate,
    setEndDate,
    setArtists
  } = useEventsStore()

  const handleSelect = (newDate: DayjsInput) => {
    startTransition(() => {
      setSelectedDate(newDate)
    })
  }

  const calendarDates = (selectedDate: DayjsInput) => {
    if (endDate) {
      return {
        startDate: dayjs(initialDate as string).format(DATE_FORMAT.SHORT_DATE_NO_SEPRATOR),
        endDate: dayjs(endDate as string).format(DATE_FORMAT.SHORT_DATE_NO_SEPRATOR)
      }
    }
    return generateCalendarStartAndEndDate(selectedDate)
  }

  useEffect(() => {
    const startDateFromURL = searchParams.get('startdate')?.replace(/-/g, '/')
    const resolvedDate = startDateFromURL || selectedDate
    const parsedDate = dayjs(resolvedDate as string)
    if (startDateFromURL && !initializedFromURL.current) {
      initializedFromURL.current = true
      setSelectedDate(parsedDate.toDate())
    }

    const { startDate, endDate: endRange } = calendarDates(parsedDate)

    // Set state in store
    setStartDate(startDate)
    setEndDate(dayjs(endRange, DATE_FORMAT.SHORT_DATE_NO_SEPRATOR).toDate())
    setArtists(artists)
    // Fetch events
    fetchEvents(
      dayjs(startDate, DATE_FORMAT.SHORT_DATE_NO_SEPRATOR).toDate(),
      dayjs(endRange, DATE_FORMAT.SHORT_DATE_NO_SEPRATOR).toDate(),
      artists
    )
  }, [selectedDate, JSON.stringify(artists)])

  return {
    selectedDate,
    selectedMonth,
    artists,
    errors,
    loading,
    filteredItems,
    activeMonthList,
    setSelectedDate: handleSelect
  }
}
